import type { FC } from 'react';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, xcss, Box } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

const i18n = defineMessages({
	exitFeed: {
		id: 'feed.exit-feed-button',
		defaultMessage: 'Exit feed',
		description: 'The text is shown on an accessibility button that allows users to exit the infinite-scrolling feed and return focus to the rest of the page, improving keyboard and screen reader navigation.',
	},
});

const hideStyles = xcss({
	height: '0',
	overflow: 'hidden',
});

const showStyles = xcss({
	marginBottom: 'space.150',
	height: 'auto',
});

type ExitFeedAllyButtonProps = {
	onExit: () => void;
	source: 'following-feed' | 'popular-feed';
	feedCardIndex?: number;
	isAfterRecommendations?: boolean;
};

export const ExitFeedAllyButton: FC<ExitFeedAllyButtonProps> = ({
	onExit,
	source,
	feedCardIndex,
	isAfterRecommendations,
}) => {
	const intl = useIntl();
	const [showExitFeedButton, setShowExitFeedButton] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnClick = () => {
		onExit();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'exitFeedButton',
				attributes: {
					feedCardIndex,
					isAfterRecommendations: !!isAfterRecommendations,
				},
			},
		}).fire();
	};

	const onFocus = () => {
		setShowExitFeedButton(true);
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'exitFeedButton',
				attributes: {
					feedCardIndex,
					isAfterRecommendations: !!isAfterRecommendations,
				},
			},
		}).fire();
	};

	const onBlur = () => {
		setShowExitFeedButton(false);
	};

	return (
		<Box xcss={showExitFeedButton ? showStyles : hideStyles} testId="exit-feed-wrapper">
			<Inline alignInline="center">
				<Button
					onClick={handleOnClick}
					onFocus={onFocus}
					onBlur={onBlur}
					data-vc="exit-feed-a11y-button"
				>
					{intl.formatMessage(i18n.exitFeed)}
				</Button>
			</Inline>
		</Box>
	);
};
